import React, { useState, useEffect, useCallback } from 'react';
import { authAPI } from '../services/api';
import { formatDate } from '../utils/format';
import { ROLES } from '../utils/roles';
import Navigation from '../components/Navigation';
import './Users.css';

function Users({ onLogout, onFeedback }) {
  const [users, setUsers] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Add-user form
  const [newName, setNewName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newRole, setNewRole] = useState('');
  const [addError, setAddError] = useState('');
  const [addMessage, setAddMessage] = useState('');
  const [adding, setAdding] = useState(false);

  // Change-my-password form
  const [currentPassword, setCurrentPassword] = useState('');
  const [changeNewPassword, setChangeNewPassword] = useState('');
  const [pwError, setPwError] = useState('');
  const [pwMessage, setPwMessage] = useState('');
  const [changing, setChanging] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [me, list] = await Promise.all([authAPI.me(), authAPI.listUsers()]);
      setCurrentUser(me);
      setUsers(list);
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleAddUser = async (e) => {
    e.preventDefault();
    setAddError('');
    setAddMessage('');
    setAdding(true);
    try {
      await authAPI.createUser({
        full_name: newName,
        email: newEmail,
        password: newPassword,
        role: newRole,
      });
      setAddMessage(`Added ${newName}. Share their temporary password — they'll be prompted to set their own when they first log in.`);
      setNewName('');
      setNewEmail('');
      setNewPassword('');
      setNewRole('');
      loadData();
    } catch (err) {
      setAddError(err.response?.data?.detail || 'Failed to add user.');
    } finally {
      setAdding(false);
    }
  };

  const handleToggleActive = async (user) => {
    try {
      await authAPI.setUserActive(user.id, !user.is_active);
      loadData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to update user.');
    }
  };

  const handleToggleAdmin = async (user) => {
    const action = user.is_admin ? 'Remove admin access from' : 'Make an admin:';
    if (!window.confirm(`${action} ${user.full_name}?`)) return;
    try {
      await authAPI.setUserAdmin(user.id, !user.is_admin);
      loadData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to update admin access.');
    }
  };

  const handleChangeRole = async (user, role) => {
    if (!role || role === user.role) return;
    try {
      await authAPI.setUserRole(user.id, role);
      loadData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to update role.');
    }
  };

  const handleResetPassword = async (user) => {
    const newPw = window.prompt(
      `Enter a new password for ${user.full_name} (min 8 characters):`
    );
    if (!newPw) return;
    try {
      await authAPI.resetUserPassword(user.id, newPw);
      alert(`Password reset for ${user.full_name}.`);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to reset password.');
    }
  };

  const handleChangeMyPassword = async (e) => {
    e.preventDefault();
    setPwError('');
    setPwMessage('');
    setChanging(true);
    try {
      await authAPI.changeMyPassword(currentPassword, changeNewPassword);
      setPwMessage('Your password has been changed.');
      setCurrentPassword('');
      setChangeNewPassword('');
    } catch (err) {
      setPwError(err.response?.data?.detail || 'Failed to change password.');
    } finally {
      setChanging(false);
    }
  };

  return (
    <div>
      <Navigation onLogout={onLogout} onFeedback={onFeedback} />
      <div className="container">
        <div className="page-header">
          <h1>Users</h1>
        </div>

        {loading ? (
          <p className="users-loading">Loading…</p>
        ) : (
          <div className="users-table-wrap">
            <table className="candidate-table users-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Added</th>
                  <th className="col-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => {
                  const isSelf = currentUser && user.id === currentUser.id;
                  return (
                    <tr key={user.id} className={user.is_active ? '' : 'user-inactive'}>
                      <td className="user-name-cell">
                        {user.full_name}
                        {isSelf && <span className="you-badge">You</span>}
                        {user.is_admin && <span className="admin-badge">Admin</span>}
                      </td>
                      <td className="user-email-cell">{user.email}</td>
                      <td className="user-role-cell">
                        <select
                          className="role-select"
                          value={user.role || ''}
                          onChange={(e) => handleChangeRole(user, e.target.value)}
                          title="Content-access role"
                        >
                          {!user.role && <option value="" disabled>— no role —</option>}
                          {ROLES.map((r) => (
                            <option key={r.value} value={r.value}>{r.label}</option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <span
                          className={`status-badge ${user.is_active ? 'status-active' : 'status-inactive'}`}
                        >
                          {user.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>{user.created_at ? formatDate(user.created_at) : '—'}</td>
                      <td className="user-actions">
                        <button
                          className="btn-small btn-secondary"
                          onClick={() => handleResetPassword(user)}
                        >
                          Reset password
                        </button>
                        {!isSelf && (
                          <button
                            className="btn-small btn-secondary"
                            onClick={() => handleToggleAdmin(user)}
                          >
                            {user.is_admin ? 'Remove admin' : 'Make admin'}
                          </button>
                        )}
                        {!isSelf && (
                          <button
                            className={`btn-small ${user.is_active ? 'btn-danger' : 'btn-success'}`}
                            onClick={() => handleToggleActive(user)}
                          >
                            {user.is_active ? 'Deactivate' : 'Activate'}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className="users-panels">
          <div className="users-panel">
            <h2>Add a user</h2>
            <p className="panel-hint">
              New staff sign in with their work email. Set a temporary password and
              share it with them — they'll be required to choose their own the
              first time they log in.
            </p>
            {addError && <div className="error-message">{addError}</div>}
            {addMessage && <div className="success-message">{addMessage}</div>}
            <form onSubmit={handleAddUser}>
              <div className="form-group">
                <label>Full name</label>
                <input
                  type="text"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  required
                  disabled={adding}
                />
              </div>
              <div className="form-group">
                <label>Work email</label>
                <input
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  required
                  disabled={adding}
                />
              </div>
              <div className="form-group">
                <label>Temporary password (min 8 characters)</label>
                <input
                  type="text"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  minLength={8}
                  required
                  disabled={adding}
                />
              </div>
              <div className="form-group">
                <label>Role (candidate access)</label>
                <select
                  value={newRole}
                  onChange={(e) => setNewRole(e.target.value)}
                  required
                  disabled={adding}
                >
                  <option value="" disabled>Select a role…</option>
                  {ROLES.map((r) => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
              </div>
              <button type="submit" className="btn-primary" disabled={adding}>
                {adding ? 'Adding…' : 'Add user'}
              </button>
            </form>
          </div>

          <div className="users-panel">
            <h2>Change my password</h2>
            <p className="panel-hint">Update the password for your own account.</p>
            {pwError && <div className="error-message">{pwError}</div>}
            {pwMessage && <div className="success-message">{pwMessage}</div>}
            <form onSubmit={handleChangeMyPassword}>
              <div className="form-group">
                <label>Current password</label>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  required
                  disabled={changing}
                />
              </div>
              <div className="form-group">
                <label>New password (min 8 characters)</label>
                <input
                  type="password"
                  value={changeNewPassword}
                  onChange={(e) => setChangeNewPassword(e.target.value)}
                  minLength={8}
                  required
                  disabled={changing}
                />
              </div>
              <button type="submit" className="btn-primary" disabled={changing}>
                {changing ? 'Saving…' : 'Change password'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Users;
